<template>
  <div
    ref="sliderRef"
    class="w-full flex-1 glass-frosted rounded-8xl overflow-hidden cursor-pointer relative"
    @mousedown="startDrag"
  >
    <div
      class="bg-white h-full rounded-8xl"
      :style="{
        width: `${modelValue}%`,
      }"
    ></div>
    <div class="absolute p-5 inset-0 flex items-center pointer-events-none">
      <Icon icon="Status-Wireframe-Day" class="size-10" />
      <div class="flex flex-col gap-1 ml-2">
        <div class="text-3xl font-bold">{{ modelValue }} %</div>
        <div class="text-m-medium">{{ $t('lighting.brightness') }}</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { ref } from 'vue'
  import { Icon } from '@rapoo/element'
  import { debounce } from '@/utils/optimize'
  import { useSliderDrag } from '@/composables/useSliderDrag'

  const props = withDefaults(defineProps<{ modelValue: number }>(), {
    modelValue: 60,
  })
  const emit = defineEmits(['update:modelValue', 'change'])

  // 拖拽实现
  const sliderRef = ref<HTMLElement | null>(null)
  const { startDrag } = useSliderDrag(sliderRef, {
    onChange(percentage) {
      emit('update:modelValue', Math.round(percentage))
    },
    onEnd(percentage) {
      emit('change', Math.round(percentage))
    },
  })

  // 快捷键实现
  import { useCombinationKey } from '@/composables/useCombinationKey'
  import { useActiveState } from '@/composables/useComponentsState'
  useActiveState(sliderRef, {
    autoListen: true,
    onBlur: () => {
      unregisterShortcuts()
    },
    onFocus: () => {
      registerShortcuts()
    },
  })
  const increaseBrightness = (step: number = 5) => {
    const newValue = Math.min(100, props.modelValue + step)
    emit('update:modelValue', newValue)
  }
  const decreaseBrightness = (step: number = 5) => {
    const newValue = Math.max(0, props.modelValue - step)
    emit('update:modelValue', newValue)
  }
  let shortcutHandlers: { unregister: () => void }[] = []
  const registerShortcuts = () => {
    const { unregister: unregisterRight } = useCombinationKey(
      'arrowright',
      debounce((e: KeyboardEvent) => {
        e.preventDefault()
        increaseBrightness(1)
      }, 100),
      { continuous: true },
    )
    const { unregister: unregisterLeft } = useCombinationKey(
      'arrowleft',
      debounce((e: KeyboardEvent) => {
        e.preventDefault()
        decreaseBrightness(1)
      }, 100),
      { continuous: true },
    )
    shortcutHandlers = [{ unregister: unregisterRight }, { unregister: unregisterLeft }]
  }

  const unregisterShortcuts = () => {
    shortcutHandlers.forEach((handler) => {
      handler.unregister()
    })
    shortcutHandlers = []
  }
</script>
